import { useEffect, useMemo, useRef, useState } from 'react'
import { useVirtualizer } from '@tanstack/react-virtual'
import { ensureMessagesVisible } from '../lib/prefetch'
import type { Conversation, Participant } from '../lib/db'
import { ActionTooltip, Kbd } from './Kbd'
import TagBadge from './TagBadge'
import type { Tag } from '../hooks/useTags'

interface ConversationListProps {
  conversations: Conversation[]
  selectedUrn: string | null
  highlightedUrn?: string | null
  onSelect: (conversationUrn: string) => void
  searchMode?: boolean
  onLoadMore?: () => void
  getDraftText?: (conversationUrn: string) => string | null
  onArchive?: (conversationUrn: string) => void
  showArchiveAction?: boolean
  allTags?: Tag[]
  getTagsForConversation?: (conversationUrn: string) => string[]
  selectedUrns?: Set<string>
  onMultiSelect?: (conversationUrn: string, event: React.MouseEvent) => void
  onGroupSelect?: (conversationUrns: string[]) => void
  activeFilter?: string
  activeCategory?: string
  onCategoryChange?: (category: string) => void
  categoryCounts?: Record<string, number>
  categoryChips?: readonly { key: string; label: string }[]
  exitingUrns?: Map<string, string>
  onExitComplete?: (conversationUrn: string) => void
  initialized?: boolean
  /**
   * Active Inbox view mode (Kondo-style filter menu). Only set when the
   * active filter is INBOX; undefined on any other filter. Drives empty-
   * state copy so "No conversations" becomes "All caught up" (UNREAD),
   * "Nothing waiting on them" (WAITING_THEIRS), etc.
   */
  inboxView?: 'ALL' | 'WAITING_THEIRS' | 'WAITING_MINE' | 'UNREAD'
}

type ListItem =
  | { type: 'header'; label: string }
  | { type: 'conversation'; conversation: Conversation }

export default function ConversationList({ conversations, selectedUrn, highlightedUrn, onSelect, searchMode = false, onLoadMore, getDraftText, onArchive, showArchiveAction, allTags, getTagsForConversation, selectedUrns, onMultiSelect, onGroupSelect, activeFilter, activeCategory, onCategoryChange, categoryCounts, categoryChips, exitingUrns, onExitComplete, initialized = true, inboxView }: ConversationListProps) {
  const parentRef = useRef<HTMLDivElement>(null)
  const [hoveredUrn, setHoveredUrn] = useState<string | null>(null)

  // No date grouping in search mode — flat relevance-ranked list
  const items = useMemo(() =>
    searchMode ? flatList(conversations) : groupByTime(conversations),
    [conversations, searchMode]
  )

  const virtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => parentRef.current,
    estimateSize: (index) => (items[index].type === 'header' ? 32 : 72),
    overscan: 10,
    // offsetHeight reads the element's layout box and is unaffected by any
    // transform on the element — getBoundingClientRect().height can return
    // subtly different values mid-transition because gBCR is the transformed
    // visual rect. With a `transition: transform` on the wrapper (below) and
    // a visibilitychange → .measure() loop (removed below), gBCR measurements
    // drifted larger every time the user tabbed away and back, inflating the
    // virtualizer's cached sizes and pushing rows further apart. Credit to a
    // sharp review catching this — see commit message.
    measureElement: (el) => (el as HTMLElement).offsetHeight,
    // Stable keys so measurements stick to the item, not the index position
    getItemKey: (index) => {
      const item = items[index]
      return item.type === 'header' ? `h-${item.label}` : item.conversation.conversationUrn
    },
  })

  // Scroll highlighted conversation into view (works with virtualized list)
  useEffect(() => {
    if (!highlightedUrn) return
    const idx = items.findIndex(
      (item) => item.type === 'conversation' && item.conversation.conversationUrn === highlightedUrn
    )
    if (idx >= 0) virtualizer.scrollToIndex(idx, { align: 'auto' })
  }, [highlightedUrn, items, virtualizer])

  // (Removed: a visibilitychange → virtualizer.measure() handler that was
  // meant as a defensive "rebuild after sleep/wake staleness" but actually
  // caused the bug it was guarding against — every tab-return triggered a
  // remeasure, which combined with a transform transition on row wrappers
  // produced drifting row heights and visibly growing gaps between items.
  // ResizeObserver + a stable measureElement already cover the real
  // staleness cases; we don't need to force-kick the virtualizer on tab
  // focus.)

  const visibleItems = virtualizer.getVirtualItems()
  useEffect(() => {
    const visibleUrns = visibleItems
      .map((vi) => {
        const item = items[vi.index]
        return item.type === 'conversation' ? item.conversation.conversationUrn : null
      })
      .filter((u): u is string => !!u)
    if (visibleUrns.length > 0) ensureMessagesVisible(visibleUrns)
  }, [visibleItems, items])

  const visibleChips = useMemo(() => {
    if (!categoryChips || !categoryCounts || !onCategoryChange) return null
    return categoryChips.filter((c) => c.key === 'ALL' || (categoryCounts[c.key] || 0) > 0)
  }, [categoryChips, categoryCounts, onCategoryChange])

  // Intentionally no early return for `conversations.length === 0` here.
  // A previous early-return preempted the skeleton loader at the start of
  // a filter fetch AND the per-filter designed empty-states lower in this
  // component ("All caught up", "Nothing waiting on them", etc.). The
  // downstream `!initialized && length === 0` (skeleton) and
  // `initialized && length === 0` (empty copy) branches already handle
  // both cases — just need to be reachable.

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      {visibleChips && visibleChips.length > 1 && (
        <div className="flex gap-1.5 overflow-x-auto border-b border-zinc-100 px-4 py-2.5">
          {visibleChips.map((chip) => {
            const isActive = activeCategory === chip.key
            const count = categoryCounts?.[chip.key] || 0
            return (
              <button
                key={chip.key}
                onClick={() => onCategoryChange?.(chip.key)}
                className={`shrink-0 rounded-full px-2.5 py-1 text-[11px] font-medium transition-colors ${
                  isActive
                    ? 'bg-zinc-900 text-white'
                    : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'
                }`}
              >
                {chip.label}{count > 0 ? ` (${count})` : ''}
              </button>
            )
          })}
        </div>
      )}
    <div
      ref={parentRef}
      className="flex-1 overflow-y-auto"
      onScroll={() => {
        if (!onLoadMore || !parentRef.current) return
        const el = parentRef.current
        if (el.scrollTop + el.clientHeight >= el.scrollHeight - 100) {
          onLoadMore()
        }
      }}
    >
      {!initialized && conversations.length === 0 ? (
        <div className="flex flex-col gap-1 px-2 py-2">
          {[0.9, 0.7, 0.5, 0.35, 0.2].map((opacity, i) => (
            <div key={i} className="flex items-center gap-3 rounded-lg px-3 py-3" style={{ opacity }}>
              <div className="h-10 w-10 animate-pulse rounded-full bg-zinc-200" />
              <div className="flex-1 space-y-2">
                <div className="h-3 w-24 animate-pulse rounded bg-zinc-200" />
                <div className="h-2.5 w-40 animate-pulse rounded bg-zinc-100" />
              </div>
            </div>
          ))}
        </div>
      ) : initialized && conversations.length === 0 ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-1 p-4 pt-20 text-zinc-400">
          <span className="text-sm">{
            inboxView === 'UNREAD' ? 'All caught up' :
            inboxView === 'WAITING_THEIRS' ? 'Nothing waiting on them' :
            inboxView === 'WAITING_MINE' ? 'Nothing waiting on you' :
            activeFilter === 'ARCHIVE' ? 'No archived conversations' :
            activeFilter === 'REMINDERS' ? 'No reminders set' :
            activeFilter === 'STARRED' ? 'No starred conversations' :
            activeFilter === 'DRAFTS' ? 'No drafts' :
            'No conversations'
          }</span>
          {inboxView === 'UNREAD' && (
            <span className="text-xs">All messages have been read</span>
          )}
        </div>
      ) : (
      <div style={{ height: `${virtualizer.getTotalSize()}px`, position: 'relative' }}>
        {visibleItems.map((virtualItem) => {
          const item = items[virtualItem.index]

          if (item.type === 'header') {
            return (
              <div
                key={`h-${item.label}`}
                ref={virtualizer.measureElement}
                data-index={virtualItem.index}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  transform: `translateY(${virtualItem.start}px)`,
                }}
              >
                <div
                  className="flex cursor-pointer items-center px-4 pb-1 pt-3"
                  onClick={() => {
                    if (!onGroupSelect) return
                    const groupUrns: string[] = []
                    for (let i = virtualItem.index + 1; i < items.length; i++) {
                      const next = items[i]
                      if (next.type === 'header') break
                      groupUrns.push(next.conversation.conversationUrn)
                    }
                    onGroupSelect(groupUrns)
                  }}
                >
                  <span className="text-[10px] font-medium tracking-wide text-zinc-400 hover:text-zinc-600">
                    {item.label}
                  </span>
                </div>
              </div>
            )
          }

          const conv = item.conversation
          const isSelected = conv.conversationUrn === selectedUrn
          const isHighlighted = conv.conversationUrn === highlightedUrn && !isSelected
          const isExiting = exitingUrns?.has(conv.conversationUrn)

          return (
            <div
              key={conv.conversationUrn}
              ref={virtualizer.measureElement}
              data-index={virtualItem.index}
              data-conversation-urn={conv.conversationUrn}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                transform: `translateY(${virtualItem.start}px)`,
              }}
            >
             <div
              style={isExiting ? { animation: 'slide-out-left 300ms ease-out forwards', pointerEvents: 'none' as const } : undefined}
              onAnimationEnd={isExiting ? () => onExitComplete?.(conv.conversationUrn) : undefined}
             >
              <div
                onMouseDown={(e) => {
                  // Prevent browser text-range selection on shift/ctrl+click
                  if (e.shiftKey || e.metaKey || e.ctrlKey) e.preventDefault()
                }}
                onClick={(e) => {
                  if (e.metaKey || e.ctrlKey || e.shiftKey) {
                    onMultiSelect?.(conv.conversationUrn, e)
                  } else {
                    onSelect(conv.conversationUrn)
                  }
                }}
                onMouseEnter={() => setHoveredUrn(conv.conversationUrn)}
                onMouseLeave={() => setHoveredUrn(null)}
                role="button"
                tabIndex={0}
                className={`flex w-full cursor-pointer items-start gap-3 px-4 py-2.5 text-left transition-colors ${
                  selectedUrns?.size ? 'select-none ' : ''
                }${
                  selectedUrns?.has(conv.conversationUrn)
                    ? 'border-l-2 border-blue-400 bg-blue-50'
                    : isSelected && !selectedUrns?.size
                      ? 'border-l-2 border-blue-500 bg-blue-50/60'
                      : isHighlighted
                        ? 'bg-zinc-100'
                        : 'hover:bg-zinc-50'
                }`}
              >
                <div className="relative h-9 w-9 shrink-0">
                  {conv.isGroup ? (
                    <div className={selectedUrns?.has(conv.conversationUrn) ? 'opacity-40' : ''}>
                      <GroupAvatars participants={conv.allParticipants || []} isUnread={!conv.isRead} />
                    </div>
                  ) : conv.participantProfileImage ? (
                    <img src={conv.participantProfileImage} alt="" className={`h-9 w-9 rounded-full ${selectedUrns?.has(conv.conversationUrn) ? 'opacity-40' : ''}`} />
                  ) : (
                    <div className={`flex h-9 w-9 items-center justify-center rounded-full text-xs font-semibold ${
                      selectedUrns?.has(conv.conversationUrn)
                        ? 'bg-blue-100/40 text-blue-600/40'
                        : !conv.isRead
                          ? 'bg-blue-100 text-blue-600'
                          : 'bg-zinc-100 text-zinc-500'
                    }`}>
                      {getInitials(conv.participantName)}
                    </div>
                  )}
                  {selectedUrns?.has(conv.conversationUrn) && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    </div>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between">
                    <span className={`truncate text-[13px] ${
                      !conv.isRead ? 'font-semibold text-zinc-900' : 'text-zinc-600'
                    }`}>
                      {conv.participantName}
                    </span>
                    {conv.category === 'SALESNAV' && activeFilter !== 'SALESNAV' && (
                      <span className="ml-1 shrink-0 rounded bg-blue-50 px-1 py-0.5 text-[9px] font-medium text-blue-600">SN</span>
                    )}
                    <div className="ml-2 flex shrink-0 items-center gap-1.5">
                      {getDraftText?.(conv.conversationUrn) && (
                        <span className="text-[10px] font-medium text-amber-500">Draft</span>
                      )}
                      <span className={`text-[11px] ${
                        !conv.isRead ? 'font-medium text-blue-500' : 'text-zinc-400'
                      }`}>
                        {formatShortTime(conv.lastMessageTimestamp)}
                      </span>
                    </div>
                  </div>
                  {getDraftText?.(conv.conversationUrn) ? (
                    <p className="truncate text-xs text-amber-600">
                      {getDraftText(conv.conversationUrn)}
                    </p>
                  ) : (
                    <p className={`truncate text-xs ${
                      !conv.isRead ? 'font-medium text-zinc-600' : 'text-zinc-400'
                    }`}>
                      {conv.lastMessageText}
                    </p>
                  )}
                  {(() => {
                    const convTagIds = getTagsForConversation?.(conv.conversationUrn) ?? []
                    const convTags = convTagIds.map((id) => allTags?.find((t) => t.id === id)).filter(Boolean) as Tag[]
                    if (convTags.length === 0) return null
                    return (
                      <div className="mt-0.5 flex gap-1 overflow-hidden">
                        {convTags.slice(0, 3).map((tag) => (
                          <TagBadge key={tag.id} name={tag.name} color={tag.color} icon={tag.icon} shared={!!tag.team_id} />
                        ))}
                        {convTags.length > 3 && <span className="text-[10px] text-zinc-400">+{convTags.length - 3}</span>}
                      </div>
                    )
                  })()}
                </div>
                {showArchiveAction && hoveredUrn === conv.conversationUrn ? (
                  <ActionTooltip label={conv.category === 'ARCHIVE' ? 'Unarchive' : 'Archive'} shortcut={<Kbd>E</Kbd>}>
                    <button
                      onClick={(e) => { e.stopPropagation(); onArchive?.(conv.conversationUrn) }}
                      className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-zinc-400 hover:bg-zinc-200 hover:text-zinc-600"
                    >
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="21 8 21 21 3 21 3 8" /><rect x="1" y="3" width="22" height="5" /><line x1="10" y1="12" x2="14" y2="12" />
                      </svg>
                    </button>
                  </ActionTooltip>
                ) : !conv.isRead ? (
                  conv.unreadCount > 1 ? (
                    <span className="mt-1 shrink-0 rounded-full bg-blue-500 px-1.5 text-[10px] font-semibold text-white">
                      {conv.unreadCount}
                    </span>
                  ) : (
                    <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-blue-500" />
                  )
                ) : null}
              </div>
             </div>
            </div>
          )
        })}
      </div>
      )}
    </div>
    </div>
  )
}

function getInitials(name: string): string {
  return name.split(' ').map((p) => p[0]).join('').slice(0, 2).toUpperCase()
}

function flatList(conversations: Conversation[]): ListItem[] {
  if (conversations.length === 0) return []
  return [
    { type: 'header' as const, label: 'Results' },
    ...conversations.map((c) => ({ type: 'conversation' as const, conversation: c })),
  ]
}

function groupByTime(conversations: Conversation[]): ListItem[] {
  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const weekAgo = new Date(today.getTime() - 7 * 86_400_000)

  const items: ListItem[] = []
  let currentGroup = ''

  for (const conv of conversations) {
    const date = new Date(conv.lastMessageTimestamp)
    const group = getTimeGroup(date, today, weekAgo)

    if (group !== currentGroup) {
      currentGroup = group
      items.push({ type: 'header', label: group })
    }
    items.push({ type: 'conversation', conversation: conv })
  }

  return items
}

function getTimeGroup(date: Date, today: Date, weekAgo: Date): string {
  if (date >= today) return 'Today'
  const yesterday = new Date(today.getTime() - 86_400_000)
  if (date >= yesterday) return 'Yesterday'
  if (date >= weekAgo) return 'Last 7 days'
  const month = date.toLocaleString('default', { month: 'long' })
  if (date.getFullYear() !== today.getFullYear()) return `${month} ${date.getFullYear()}`
  return month
}

function formatShortTime(ts: number): string {
  if (!ts) return ''
  const date = new Date(ts)
  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())

  if (date >= today) {
    return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
  }
  const yesterday = new Date(today.getTime() - 86_400_000)
  if (date >= yesterday) return 'Yesterday'
  const weekAgo = new Date(today.getTime() - 7 * 86_400_000)
  if (date >= weekAgo) return date.toLocaleDateString([], { weekday: 'short' })
  return date.toLocaleDateString([], { day: 'numeric', month: 'short' })
}

function GroupAvatars({ participants, isUnread }: { participants: Participant[]; isUnread: boolean }) {
  const shown = participants.slice(0, 3)
  const extra = participants.length - 3
  return (
    <div className="flex shrink-0 -space-x-2">
      {shown.map((p, i) => {
        const k = p.profileUrn ?? `${p.name}-${i}`
        return p.profileImage ? (
          <img
            key={k}
            src={p.profileImage}
            alt=""
            className="h-7 w-7 rounded-full border-2 border-white"
            style={{ zIndex: 3 - i }}
          />
        ) : (
          <div
            key={k}
            className={`flex h-7 w-7 items-center justify-center rounded-full border-2 border-white text-[9px] font-semibold ${
              isUnread ? 'bg-blue-100 text-blue-600' : 'bg-zinc-100 text-zinc-500'
            }`}
            style={{ zIndex: 3 - i }}
          >
            {getInitials(p.name)}
          </div>
        )
      })}
      {extra > 0 && (
        <div
          className="flex h-7 w-7 items-center justify-center rounded-full border-2 border-white bg-zinc-200 text-[9px] font-semibold text-zinc-600"
          style={{ zIndex: 0 }}
        >
          +{extra}
        </div>
      )}
    </div>
  )
}
